import React, { useState, useEffect, useCallback } from 'react';
import Cookies from 'js-cookie';
import apiService from '../../shared/services/ApiService';
import socketService from '../../shared/services/socket';
import ReporteDetailModal from './ReporteDetailModal';
import MapaReportes from './MapaReportes';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import QRCode from 'qrcode';
import '../../css/adminReportesCiudadanos.css';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const STATUS_COLORS = {
    'Recibido': '#95a5a6',
    'Revisado': '#3498db',
    'Asignado': '#f39c12',
    'En proceso': '#e67e22',
    'Resuelto': '#27ae60',
    'Cerrado': '#7f8c8d'
};

const CATEGORY_LABELS = {
    iluminacion: 'Iluminacion',
    bancos: 'Bancos',
    basura: 'Basura',
    vegetacion: 'Vegetacion',
    infraestructura: 'Infraestructura',
    otro: 'Otro'
};

const PRIORITY_LABELS = { baja: 'Baja', media: 'Media', alta: 'Alta' };
const PRIORITY_COLORS = { baja: '#95a5a6', media: '#f39c12', alta: '#e74c3c' };

const AdminReportesCiudadanos = () => {
    const [reportes, setReportes] = useState([]);
    const [brigades, setBrigades] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedReporte, setSelectedReporte] = useState(null);
    const [sectors, setSectors] = useState([]);
    const [viewMode, setViewMode] = useState('table'); // table | dashboard | mapa

    // Permisos
    const permissions = JSON.parse(Cookies.get('permissions') || '[]');
    const canViewAll = permissions.includes('reportes_ciudadanos:ver');
    const canAssign = permissions.includes('reportes_ciudadanos:asignar');
    const canChangeStatus = permissions.includes('reportes_ciudadanos:estado');
    const canComment = permissions.includes('reportes_ciudadanos:comentar');
    const canDelete = permissions.includes('reportes_ciudadanos:eliminar') || permissions.includes('reportes_ciudadanos:gestionar');
    const canExport = permissions.includes('reportes_ciudadanos:exportar') || permissions.includes('reportes_ciudadanos:gestionar');

    // Filters
    const [statusFilter, setStatusFilter] = useState('all');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [priorityFilter, setPriorityFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');

    // Date filters
    const [dateFilter, setDateFilter] = useState('month');
    const [customStartDate, setCustomStartDate] = useState('');
    const [customEndDate, setCustomEndDate] = useState('');

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 12;

    const getDateRange = useCallback(() => {
        const today = new Date();
        const fmt = (d) => d.toISOString().split('T')[0];
        switch (dateFilter) {
            case 'today':
                return { fechaInicio: fmt(today), fechaFin: fmt(today) };
            case 'week': {
                const start = new Date(today);
                const day = start.getDay();
                start.setDate(start.getDate() - (day === 0 ? 6 : day - 1));
                return { fechaInicio: fmt(start), fechaFin: fmt(today) };
            }
            case 'month': {
                const start = new Date(today.getFullYear(), today.getMonth(), 1);
                return { fechaInicio: fmt(start), fechaFin: fmt(today) };
            }
            case 'custom':
                if (customStartDate && customEndDate) {
                    return { fechaInicio: customStartDate, fechaFin: customEndDate };
                }
                return {};
            case 'all':
            default:
                return {};
        }
    }, [dateFilter, customStartDate, customEndDate]);

    const fetchReportes = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (statusFilter !== 'all') params.append('status', statusFilter);
            if (categoryFilter !== 'all') params.append('category', categoryFilter);
            if (priorityFilter !== 'all') params.append('priority', priorityFilter);

            const { fechaInicio, fechaFin } = getDateRange();
            if (fechaInicio) params.append('fechaInicio', fechaInicio);
            if (fechaFin) params.append('fechaFin', fechaFin);

            // Si solo puede ver asignados, usar endpoint especifico
            let url;
            if (canViewAll) {
                url = `/api/reportes-ciudadanos/admin/all?${params.toString()}`;
            } else {
                url = `/api/reportes-ciudadanos/my-assigned?${params.toString()}`;
            }

            const response = await apiService.get(url);
            setReportes(response.data);

            // Fetch stats con mismo rango de fecha
            if (canViewAll) {
                const statsParams = new URLSearchParams();
                if (fechaInicio) statsParams.append('fechaInicio', fechaInicio);
                if (fechaFin) statsParams.append('fechaFin', fechaFin);
                try {
                    const statsRes = await apiService.get(`/api/reportes-ciudadanos/admin/stats?${statsParams.toString()}`);
                    setStats(statsRes.data);
                } catch (e) {
                    console.error('Error fetching stats:', e);
                }
            }
        } catch (err) {
            toast.error('Error al cargar reportes');
        } finally {
            setLoading(false);
        }
    }, [statusFilter, categoryFilter, priorityFilter, canViewAll, getDateRange]);

    const fetchBrigades = async () => {
        try {
            const response = await apiService.get('/api/brigadas');
            setBrigades(response.data);
        } catch (err) {
            console.error('Error fetching brigades:', err);
        }
    };

    const fetchSectors = async () => {
        try {
            const response = await apiService.get('/api/sectores/sectores');
            setSectors(response.data || []);
        } catch (err) {
            console.error('Error fetching sectors:', err);
        }
    };

    // --- PDF/Excel Generation ---
    const generateReportePDF = async (reporte) => {
        const pdf = new jsPDF('p', 'mm', 'a4');
        const pw = pdf.internal.pageSize.getWidth();
        const ph = pdf.internal.pageSize.getHeight();

        // QR Code generation
        const trackingUrl = `${window.location.origin}/reporte-ciudadano/seguimiento/${reporte.trackingCode}`;
        let qrDataUrl = null;
        try {
            qrDataUrl = await QRCode.toDataURL(trackingUrl, { width: 200, margin: 1, color: { dark: '#0057A6', light: '#ffffff' } });
        } catch (e) { console.error('QR generation error:', e); }

        // ---- BANNER HEADER ----
        // Main banner background
        pdf.setFillColor(0, 87, 166);
        pdf.rect(0, 0, pw, 52, 'F');
        // Accent stripe
        pdf.setFillColor(26, 26, 46);
        pdf.rect(0, 52, pw, 3, 'F');

        // Banner text
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.text('PARQUES PARA TODOS', 14, 12);
        pdf.setFontSize(22);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Reporte Ciudadano', 14, 24);

        // Tracking code badge
        pdf.setFillColor(255, 255, 255, 0.2);
        const codeText = reporte.trackingCode || '';
        const codeW = pdf.getTextWidth(codeText) * 0.6 + 14;
        pdf.setFillColor(26, 26, 46);
        pdf.roundedRect(14, 28, codeW, 9, 2, 2, 'F');
        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(255, 255, 255);
        pdf.text(codeText, 14 + codeW / 2, 34, { align: 'center' });

        // Date on banner
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(200, 220, 255);
        pdf.text(`Generado: ${new Date().toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })}`, 14, 44);

        // QR Code on right side of banner
        if (qrDataUrl) {
            pdf.setFillColor(255, 255, 255);
            pdf.roundedRect(pw - 44, 4, 40, 40, 2, 2, 'F');
            pdf.addImage(qrDataUrl, 'PNG', pw - 42, 6, 36, 36);
            pdf.setFontSize(5.5);
            pdf.setTextColor(200, 220, 255);
            pdf.text('Escanear para seguimiento', pw - 24, 49, { align: 'center' });
        }

        let y = 64;

        // ---- STATUS BADGE ----
        const statusColor = STATUS_COLORS[reporte.status] || '#95a5a6';
        const sc = parseInt(statusColor.slice(1, 3), 16);
        const sg = parseInt(statusColor.slice(3, 5), 16);
        const sb = parseInt(statusColor.slice(5, 7), 16);
        pdf.setFillColor(sc, sg, sb);
        const statusText = reporte.status || '-';
        const statusW = pdf.getTextWidth(statusText) * 0.45 + 16;
        pdf.roundedRect(10, y - 5, statusW, 8, 3, 3, 'F');
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(255, 255, 255);
        pdf.text(statusText, 10 + statusW / 2, y, { align: 'center' });

        // Priority badge next to status
        if (reporte.priority === 'alta') {
            pdf.setFillColor(231, 76, 60);
            pdf.roundedRect(10 + statusW + 4, y - 5, 28, 8, 3, 3, 'F');
            pdf.setTextColor(255, 255, 255);
            pdf.setFontSize(7);
            pdf.text('PRIORIDAD ALTA', 10 + statusW + 18, y, { align: 'center' });
        }

        y += 12;

        // ---- INFO CARDS ----
        const drawCard = (label, value, x, yPos, width, accent) => {
            // Card background
            pdf.setFillColor(248, 249, 250);
            pdf.roundedRect(x, yPos, width, 20, 2, 2, 'F');
            // Left accent line
            if (accent) {
                const ar = parseInt(accent.slice(1, 3), 16);
                const ag2 = parseInt(accent.slice(3, 5), 16);
                const ab = parseInt(accent.slice(5, 7), 16);
                pdf.setFillColor(ar, ag2, ab);
                pdf.rect(x, yPos + 2, 2.5, 16, 'F');
            }
            // Label
            pdf.setFontSize(6.5);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(140, 140, 140);
            pdf.text(label.toUpperCase(), x + 6, yPos + 7);
            // Value
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor(26, 26, 46);
            const valStr = String(value || '-');
            const valTrunc = valStr.length > 35 ? valStr.substring(0, 35) + '...' : valStr;
            pdf.text(valTrunc, x + 6, yPos + 15);
        };

        const colW = (pw - 30) / 2;
        drawCard('Reportante', reporte.reporterName, 10, y, colW, '#0057A6');
        drawCard('Ubicacion', reporte.locationName || reporte.sector?.name || 'No especificado', 10 + colW + 10, y, colW, '#27ae60');
        y += 26;
        drawCard('Fecha de Reporte', new Date(reporte.createdAt).toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' }), 10, y, colW, '#f39c12');
        drawCard('Contacto', reporte.reporterContact || '-', 10 + colW + 10, y, colW, '#9b59b6');
        y += 26;
        drawCard('Prioridad', PRIORITY_LABELS[reporte.priority] || reporte.priority || '-', 10, y, colW, '#e74c3c');
        drawCard('Categorias', (reporte.categories || []).map(c => CATEGORY_LABELS[c] || c).join(', '), 10 + colW + 10, y, colW, '#3498db');
        y += 30;

        // ---- SECTION: Description ----
        const drawSectionTitle = (title, yPos) => {
            pdf.setFillColor(0, 87, 166);
            pdf.roundedRect(10, yPos - 4, 4, 12, 1, 1, 'F');
            pdf.setFontSize(11);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(26, 26, 46);
            pdf.text(title, 18, yPos + 4);
            return yPos + 12;
        };

        y = drawSectionTitle('Descripcion', y);
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(60, 60, 60);
        const descLines = pdf.splitTextToSize(reporte.description || 'Sin descripcion', pw - 24);
        pdf.text(descLines, 14, y);
        y += descLines.length * 4.2 + 8;

        // ---- SECTION: Images ----
        if (reporte.images && reporte.images.length > 0) {
            if (y > 200) { pdf.addPage(); y = 20; }
            y = drawSectionTitle('Fotos Adjuntas (' + reporte.images.length + ')', y);

            const imgSize = 50;
            const imgGap = 6;
            const imgsPerRow = Math.floor((pw - 28) / (imgSize + imgGap));
            let imgX = 14;
            let imgRow = 0;

            for (let i = 0; i < reporte.images.length; i++) {
                const img = reporte.images[i];
                try {
                    const imgUrl = img.directLink || img.thumbnailLink;
                    if (!imgUrl) continue;

                    // Fetch image as base64
                    const response = await fetch(imgUrl);
                    const blob = await response.blob();
                    const base64 = await new Promise((resolve) => {
                        const reader = new FileReader();
                        reader.onloadend = () => resolve(reader.result);
                        reader.readAsDataURL(blob);
                    });

                    if (y + imgSize > ph - 20) { pdf.addPage(); y = 20; imgX = 14; imgRow = 0; }

                    // Light border
                    pdf.setDrawColor(220, 220, 220);
                    pdf.setLineWidth(0.3);
                    pdf.rect(imgX - 1, y - 1, imgSize + 2, imgSize + 2);
                    pdf.addImage(base64, 'JPEG', imgX, y, imgSize, imgSize);

                    imgX += imgSize + imgGap;
                    imgRow++;
                    if (imgRow >= imgsPerRow) {
                        imgRow = 0;
                        imgX = 14;
                        y += imgSize + imgGap;
                    }
                } catch (imgErr) {
                    console.error('Error cargando imagen para PDF:', imgErr);
                }
            }
            if (imgRow > 0) y += imgSize + imgGap;
            y += 4;
        }

        // ---- SECTION: Assignments ----
        if ((reporte.assignments || []).length > 0) {
            if (y > 230) { pdf.addPage(); y = 20; }
            y = drawSectionTitle('Brigadas Asignadas', y);

            const assignData = reporte.assignments.map(a => [
                a.brigade?.name || '?',
                (a.categories || []).map(c => CATEGORY_LABELS[c] || c).join(', '),
                a.status || '-',
                PRIORITY_LABELS[a.priority] || a.priority || '-'
            ]);

            const assignTable = autoTable(pdf, {
                startY: y,
                head: [['Brigada', 'Categorias', 'Estado', 'Prioridad']],
                body: assignData,
                margin: { left: 14, right: 14 },
                styles: { fontSize: 8, cellPadding: 3.5, lineColor: [230, 230, 230], lineWidth: 0.3 },
                headStyles: { fillColor: [0, 87, 166], textColor: [255, 255, 255], fontStyle: 'bold' },
                alternateRowStyles: { fillColor: [245, 248, 252] },
                theme: 'grid'
            });
            y = (assignTable?.finalY || pdf.previousAutoTable?.finalY || y + 20) + 10;
        }

        // ---- SECTION: Status History ----
        if ((reporte.statusHistory || []).length > 0) {
            if (y > 230) { pdf.addPage(); y = 20; }
            y = drawSectionTitle('Historial de Estados', y);

            const histData = [...reporte.statusHistory].reverse().map(h => {
                const statusVal = h.toStatus || h.status || '-';
                const dateVal = h.changedAt || h.date;
                const dateStr = dateVal ? new Date(dateVal).toLocaleString('es-ES', { dateStyle: 'medium', timeStyle: 'short' }) : '-';
                return [statusVal, h.comment || '-', dateStr];
            });

            autoTable(pdf, {
                startY: y,
                head: [['Estado', 'Comentario', 'Fecha']],
                body: histData,
                margin: { left: 14, right: 14 },
                styles: { fontSize: 8, cellPadding: 3.5, lineColor: [230, 230, 230], lineWidth: 0.3 },
                headStyles: { fillColor: [26, 26, 46], textColor: [255, 255, 255], fontStyle: 'bold' },
                alternateRowStyles: { fillColor: [245, 248, 252] },
                theme: 'grid'
            });
        }

        // ---- FOOTER on all pages ----
        const pageCount = pdf.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            pdf.setPage(i);
            // Footer line
            pdf.setDrawColor(0, 87, 166);
            pdf.setLineWidth(0.5);
            pdf.line(10, ph - 14, pw - 10, ph - 14);
            pdf.setFontSize(7);
            pdf.setTextColor(140, 140, 140);
            pdf.text('Parques para Todos - Reporte Ciudadano', 10, ph - 9);
            pdf.text(`Pagina ${i} de ${pageCount}`, pw - 10, ph - 9, { align: 'right' });
        }

        pdf.save(`reporte-${reporte.trackingCode || reporte._id}.pdf`);
        toast.success('PDF generado');
    };

    const generateAllReportesPDF = () => {
        const data = filteredReportes;
        if (!data.length) { toast.warning('No hay reportes para exportar'); return; }

        const pdf = new jsPDF('l', 'mm', 'a4');
        const pw = pdf.internal.pageSize.getWidth();
        const pHeight = pdf.internal.pageSize.getHeight();

        // ---- BANNER ----
        pdf.setFillColor(0, 87, 166);
        pdf.rect(0, 0, pw, 38, 'F');
        pdf.setFillColor(26, 26, 46);
        pdf.rect(0, 38, pw, 2.5, 'F');

        pdf.setTextColor(200, 220, 255);
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        pdf.text('PARQUES PARA TODOS', 14, 11);
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(18);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Reportes Ciudadanos', 14, 22);
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(200, 220, 255);
        pdf.text(`${data.length} reportes | ${new Date().toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })}`, 14, 32);

        // Filters applied (if any)
        const activeFilters = [];
        if (statusFilter !== 'all') activeFilters.push(`Estado: ${statusFilter}`);
        if (categoryFilter !== 'all') activeFilters.push(`Categoria: ${CATEGORY_LABELS[categoryFilter]}`);
        if (priorityFilter !== 'all') activeFilters.push(`Prioridad: ${PRIORITY_LABELS[priorityFilter]}`);
        if (activeFilters.length > 0) {
            pdf.setFontSize(7);
            pdf.text(`Filtros: ${activeFilters.join(' | ')}`, pw - 14, 32, { align: 'right' });
        }

        // ---- STAT CARDS ----
        let y = 46;
        const statItems = [
            { label: 'Total', value: data.length, color: [0, 87, 166] },
            { label: 'Recibidos', value: data.filter(r => r.status === 'Recibido').length, color: [149, 165, 166] },
            { label: 'Revisados', value: data.filter(r => r.status === 'Revisado').length, color: [52, 152, 219] },
            { label: 'En Proceso', value: data.filter(r => r.status === 'En proceso' || r.status === 'Asignado').length, color: [243, 156, 18] },
            { label: 'Resueltos', value: data.filter(r => r.status === 'Resuelto' || r.status === 'Cerrado').length, color: [39, 174, 96] },
            { label: 'Prioridad Alta', value: data.filter(r => r.priority === 'alta').length, color: [231, 76, 60] },
        ];

        const boxW = (pw - 20 - (statItems.length - 1) * 5) / statItems.length;
        statItems.forEach((s, i) => {
            const bx = 10 + i * (boxW + 5);
            // Card background
            pdf.setFillColor(248, 249, 250);
            pdf.roundedRect(bx, y, boxW, 18, 2, 2, 'F');
            // Top color accent
            pdf.setFillColor(...s.color);
            pdf.roundedRect(bx, y, boxW, 3, 2, 2, 'F');
            pdf.rect(bx, y + 1.5, boxW, 1.5, 'F');
            // Value
            pdf.setFontSize(14);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(...s.color);
            pdf.text(String(s.value), bx + boxW / 2, y + 11, { align: 'center' });
            // Label
            pdf.setFontSize(6.5);
            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor(120, 120, 120);
            pdf.text(s.label, bx + boxW / 2, y + 16, { align: 'center' });
        });

        y += 24;

        // ---- TABLE ----
        const tableData = data.map(r => [
            r.trackingCode || '-',
            r.reporterName || '-',
            (r.categories || []).map(c => CATEGORY_LABELS[c] || c).join(', '),
            r.locationName || r.sector?.name || '-',
            r.status || '-',
            PRIORITY_LABELS[r.priority] || r.priority || '-',
            (r.assignments || []).map(a => a.brigade?.name || '?').join(', ') || '-',
            new Date(r.createdAt).toLocaleDateString('es-ES')
        ]);

        autoTable(pdf, {
            startY: y,
            head: [['Codigo', 'Reportante', 'Categorias', 'Ubicacion', 'Estado', 'Prioridad', 'Brigadas', 'Fecha']],
            body: tableData,
            margin: { left: 10, right: 10 },
            styles: { fontSize: 7, cellPadding: 2.8, lineColor: [230, 230, 230], lineWidth: 0.3 },
            headStyles: { fillColor: [0, 87, 166], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 7.5 },
            alternateRowStyles: { fillColor: [245, 248, 252] },
            columnStyles: {
                0: { cellWidth: 30 },
                2: { cellWidth: 40 },
                6: { cellWidth: 35 },
            },
            theme: 'grid'
        });

        // ---- FOOTER ----
        const pageCount = pdf.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            pdf.setPage(i);
            pdf.setDrawColor(0, 87, 166);
            pdf.setLineWidth(0.5);
            pdf.line(10, pHeight - 12, pw - 10, pHeight - 12);
            pdf.setFontSize(7);
            pdf.setTextColor(140, 140, 140);
            pdf.text('Parques para Todos - Reportes Ciudadanos', 10, pHeight - 7);
            pdf.text(`Pagina ${i} de ${pageCount}`, pw - 10, pHeight - 7, { align: 'right' });
        }

        pdf.save(`reportes-ciudadanos-${new Date().toISOString().split('T')[0]}.pdf`);
        toast.success('PDF generado');
    };

    const generateReportesExcel = () => {
        const data = filteredReportes;
        if (!data.length) { toast.warning('No hay reportes para exportar'); return; }

        // Sheet 1: All reports
        const rows = data.map(r => ({
            'Codigo': r.trackingCode || '',
            'Reportante': r.reporterName || '',
            'Telefono': r.reporterPhone || '',
            'Email': r.reporterEmail || '',
            'Categorias': (r.categories || []).map(c => CATEGORY_LABELS[c] || c).join(', '),
            'Ubicacion': r.locationName || r.sector?.name || '',
            'Estado': r.status || '',
            'Prioridad': PRIORITY_LABELS[r.priority] || r.priority || '',
            'Brigadas': (r.assignments || []).map(a => a.brigade?.name || '?').join(', '),
            'Descripcion': r.description || '',
            'Fecha Creacion': new Date(r.createdAt).toLocaleDateString('es-ES'),
            'Ultima Actualizacion': r.updatedAt ? new Date(r.updatedAt).toLocaleDateString('es-ES') : ''
        }));

        const ws = XLSX.utils.json_to_sheet(rows);
        ws['!cols'] = [
            { wch: 18 }, { wch: 20 }, { wch: 14 }, { wch: 22 },
            { wch: 30 }, { wch: 22 }, { wch: 12 }, { wch: 10 },
            { wch: 25 }, { wch: 40 }, { wch: 14 }, { wch: 14 }
        ];

        // Sheet 2: Summary by status
        const byStatus = {};
        data.forEach(r => { byStatus[r.status] = (byStatus[r.status] || 0) + 1; });
        const summaryRows = Object.entries(byStatus).map(([status, count]) => ({
            'Estado': status,
            'Cantidad': count,
            'Porcentaje': `${((count / data.length) * 100).toFixed(1)}%`
        }));
        summaryRows.push({ 'Estado': 'TOTAL', 'Cantidad': data.length, 'Porcentaje': '100%' });
        const wsSummary = XLSX.utils.json_to_sheet(summaryRows);
        wsSummary['!cols'] = [{ wch: 15 }, { wch: 10 }, { wch: 12 }];

        // Sheet 3: By category
        const byCat = {};
        data.forEach(r => (r.categories || []).forEach(c => { byCat[c] = (byCat[c] || 0) + 1; }));
        const catRows = Object.entries(byCat).map(([cat, count]) => ({
            'Categoria': CATEGORY_LABELS[cat] || cat,
            'Cantidad': count
        }));
        const wsCat = XLSX.utils.json_to_sheet(catRows);
        wsCat['!cols'] = [{ wch: 20 }, { wch: 10 }];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Reportes');
        XLSX.utils.book_append_sheet(wb, wsSummary, 'Resumen');
        XLSX.utils.book_append_sheet(wb, wsCat, 'Por Categoria');

        XLSX.writeFile(wb, `reportes-ciudadanos-${new Date().toISOString().split('T')[0]}.xlsx`);
        toast.success('Excel generado');
    };

    useEffect(() => {
        fetchBrigades();
        fetchSectors();
    }, []);

    useEffect(() => {
        fetchReportes();
    }, [fetchReportes]);

    // Socket.IO: tiempo real para nuevos reportes y cambios de estado
    useEffect(() => {
        const socket = socketService.socket;
        if (!socket) return;

        const handleNuevoReporte = (data) => {
            // Agregar nuevo reporte al inicio de la lista
            setReportes(prev => [data, ...prev]);
            toast.info(`Nuevo reporte: ${data.trackingCode} - ${data.reporterName}`, { autoClose: 5000 });
        };

        const handleStatusUpdated = (data) => {
            setReportes(prev => prev.map(r =>
                r._id === data.reporteId ? { ...r, status: data.newStatus } : r
            ));
            // Actualizar detalle si está abierto
            if (selectedReporte && selectedReporte._id === data.reporteId) {
                setSelectedReporte(prev => prev ? { ...prev, status: data.newStatus } : prev);
            }
        };

        const handleReporteDeleted = (data) => {
            setReportes(prev => prev.filter(r => r._id !== data.reporteId));
            if (selectedReporte && selectedReporte._id === data.reporteId) {
                setSelectedReporte(null);
            }
        };

        const handleAssigned = (data) => {
            // Refetch para obtener datos completos de asignación
            fetchReportes();
        };

        socket.on('reporte:nuevo', handleNuevoReporte);
        socket.on('reporte:status-updated', handleStatusUpdated);
        socket.on('reporte:deleted', handleReporteDeleted);
        socket.on('reporte:assigned', handleAssigned);
        socket.on('reporte:assignment-updated', handleAssigned);

        return () => {
            socket.off('reporte:nuevo', handleNuevoReporte);
            socket.off('reporte:status-updated', handleStatusUpdated);
            socket.off('reporte:deleted', handleReporteDeleted);
            socket.off('reporte:assigned', handleAssigned);
            socket.off('reporte:assignment-updated', handleAssigned);
        };
    }, [selectedReporte]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleStatusChange = async (reporteId, newStatus, extra = {}) => {
        try {
            await apiService.put(`/api/reportes-ciudadanos/admin/${reporteId}/status`, {
                newStatus,
                ...extra
            });
            toast.success(`Estado cambiado a "${newStatus}"`);
            fetchReportes();
            if (selectedReporte && selectedReporte._id === reporteId) {
                const updated = await apiService.get(`/api/reportes-ciudadanos/admin/${reporteId}`);
                setSelectedReporte(updated.data);
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Error al cambiar estado');
        }
    };

    const handleAddAssignment = async (reporteId, assignData) => {
        try {
            const response = await apiService.post(`/api/reportes-ciudadanos/admin/${reporteId}/assignments`, assignData);
            toast.success('Brigada asignada exitosamente');
            fetchReportes();
            if (selectedReporte && selectedReporte._id === reporteId) {
                setSelectedReporte(response.data);
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Error al asignar brigada');
        }
    };

    const handleRemoveAssignment = async (reporteId, assignmentId) => {
        if (!window.confirm('Eliminar esta asignacion?')) return;
        try {
            const response = await apiService.delete(`/api/reportes-ciudadanos/admin/${reporteId}/assignments/${assignmentId}`);
            toast.success('Asignacion eliminada');
            fetchReportes();
            if (selectedReporte && selectedReporte._id === reporteId) {
                setSelectedReporte(response.data);
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Error al eliminar asignacion');
        }
    };

    const handleUpdateAssignmentStatus = async (reporteId, assignmentId, status) => {
        try {
            const response = await apiService.put(`/api/reportes-ciudadanos/admin/${reporteId}/assignments/${assignmentId}/status`, { status });
            toast.success(`Asignacion actualizada a "${status}"`);
            fetchReportes();
            if (selectedReporte && selectedReporte._id === reporteId) {
                setSelectedReporte(response.data);
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Error al actualizar asignacion');
        }
    };

    const handleAddComment = async (reporteId, text) => {
        try {
            const response = await apiService.post(`/api/reportes-ciudadanos/admin/${reporteId}/comments`, { text });
            toast.success('Comentario agregado');
            if (selectedReporte && selectedReporte._id === reporteId) {
                setSelectedReporte(response.data);
            }
        } catch (err) {
            toast.error('Error al agregar comentario');
        }
    };

    const handleDeleteReporte = async (reporteId) => {
        if (!window.confirm('¿Eliminar este reporte permanentemente? Esta accion no se puede deshacer.')) return;
        try {
            await apiService.delete(`/api/reportes-ciudadanos/admin/${reporteId}`);
            toast.success('Reporte eliminado');
            setSelectedReporte(null);
            fetchReportes();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Error al eliminar reporte');
        }
    };

    // Search filter
    const filteredReportes = reportes.filter(r => {
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();
        return r.trackingCode?.toLowerCase().includes(term) ||
            r.reporterName?.toLowerCase().includes(term) ||
            r.description?.toLowerCase().includes(term) ||
            r.locationName?.toLowerCase().includes(term);
    });

    const paginatedReportes = filteredReportes.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );
    const totalPages = Math.ceil(filteredReportes.length / itemsPerPage);

    // Dashboard stats
    const localStats = {
        total: filteredReportes.length,
        recibidos: filteredReportes.filter(r => r.status === 'Recibido').length,
        enProceso: filteredReportes.filter(r => r.status === 'En proceso' || r.status === 'Asignado').length,
        resueltos: filteredReportes.filter(r => r.status === 'Resuelto' || r.status === 'Cerrado').length,
        alta: filteredReportes.filter(r => r.priority === 'alta').length,
    };

    return (
        <div className="admin-reportes-container">
            <ToastContainer position="top-right" autoClose={3000} />

            {/* Header */}
            <div className="admin-reportes-header">
                <div>
                    <h1>Reportes Ciudadanos</h1>
                    <p>{canViewAll ? 'Gestiona los reportes enviados por ciudadanos' : 'Reportes asignados a tus brigadas'}</p>
                </div>
                <div className="header-actions-rc">
                    <div className="view-toggle-rc">
                        <button
                            className={`toggle-btn-rc ${viewMode === 'dashboard' ? 'active' : ''}`}
                            onClick={() => setViewMode('dashboard')}
                        >
                            Dashboard
                        </button>
                        <button
                            className={`toggle-btn-rc ${viewMode === 'table' ? 'active' : ''}`}
                            onClick={() => setViewMode('table')}
                        >
                            Tabla
                        </button>
                        <button
                            className={`toggle-btn-rc ${viewMode === 'mapa' ? 'active' : ''}`}
                            onClick={() => setViewMode('mapa')}
                        >
                            Mapa
                        </button>
                    </div>
                    {canExport && (
                        <div className="export-btns-group">
                            <button className="btn-export-rc pdf" onClick={generateAllReportesPDF} title="Exportar PDF">
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/>
                                </svg>
                                PDF
                            </button>
                            <button className="btn-export-rc excel" onClick={generateReportesExcel} title="Exportar Excel">
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="3" y1="15" x2="21" y2="15"/><line x1="9" y1="3" x2="9" y2="21"/><line x1="15" y1="3" x2="15" y2="21"/>
                                </svg>
                                Excel
                            </button>
                        </div>
                    )}
                    <a href="/reporte-ciudadano" target="_blank" rel="noopener noreferrer" className="btn-public-link">
                        Ver Formulario
                    </a>
                </div>
            </div>

            {/* Stats Dashboard */}
            {stats && canViewAll && (
                <div className="admin-stats-grid">
                    <div className="admin-stat-card">
                        <h3>{stats.total}</h3>
                        <p>Total</p>
                    </div>
                    {Object.entries(stats.byStatus).map(([status, count]) => (
                        count > 0 && (
                            <div key={status} className="admin-stat-card" style={{ borderTopColor: STATUS_COLORS[status] }}>
                                <h3>{count}</h3>
                                <p>{status}</p>
                            </div>
                        )
                    ))}
                    {stats.avgResolutionDays > 0 && (
                        <div className="admin-stat-card" style={{ borderTopColor: '#9b59b6' }}>
                            <h3>{stats.avgResolutionDays}d</h3>
                            <p>Tiempo Promedio</p>
                        </div>
                    )}
                    {stats.totalAssignments > 0 && (
                        <div className="admin-stat-card" style={{ borderTopColor: '#e67e22' }}>
                            <h3>{stats.completedAssignments}/{stats.totalAssignments}</h3>
                            <p>Asignaciones</p>
                        </div>
                    )}
                </div>
            )}

            {/* Mini dashboard for supervisors */}
            {!canViewAll && (
                <div className="admin-stats-grid">
                    <div className="admin-stat-card">
                        <h3>{localStats.total}</h3>
                        <p>Asignados</p>
                    </div>
                    <div className="admin-stat-card" style={{ borderTopColor: '#95a5a6' }}>
                        <h3>{localStats.recibidos}</h3>
                        <p>Pendientes</p>
                    </div>
                    <div className="admin-stat-card" style={{ borderTopColor: '#f39c12' }}>
                        <h3>{localStats.enProceso}</h3>
                        <p>En Proceso</p>
                    </div>
                    <div className="admin-stat-card" style={{ borderTopColor: '#27ae60' }}>
                        <h3>{localStats.resueltos}</h3>
                        <p>Resueltos</p>
                    </div>
                    {localStats.alta > 0 && (
                        <div className="admin-stat-card" style={{ borderTopColor: '#e74c3c' }}>
                            <h3>{localStats.alta}</h3>
                            <p>Prioridad Alta</p>
                        </div>
                    )}
                </div>
            )}

            {/* Date Filters */}
            <div className="date-filters-section">
                <div className="filters-container">
                    {[
                        { key: 'today', label: 'Hoy', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
                        { key: 'week', label: 'Esta semana', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
                        { key: 'month', label: 'Este mes', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
                        { key: 'all', label: 'Todos', icon: 'M4 6h16M4 10h16M4 14h16M4 18h16' },
                        { key: 'custom', label: 'Personalizado', icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' },
                    ].map(f => (
                        <button
                            key={f.key}
                            className={`filter-chip ${dateFilter === f.key ? 'active' : ''}`}
                            onClick={() => { setDateFilter(f.key); setCurrentPage(1); }}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d={f.icon}/>
                            </svg>
                            {f.label}
                        </button>
                    ))}
                </div>
                {dateFilter === 'custom' && (
                    <div className="custom-date-inputs">
                        <div className="date-input-group">
                            <label>Desde</label>
                            <input
                                type="date"
                                className="date-input"
                                value={customStartDate}
                                onChange={(e) => setCustomStartDate(e.target.value)}
                            />
                        </div>
                        <div className="date-input-group">
                            <label>Hasta</label>
                            <input
                                type="date"
                                className="date-input"
                                value={customEndDate}
                                onChange={(e) => setCustomEndDate(e.target.value)}
                            />
                        </div>
                        <button
                            className="apply-filter-btn"
                            onClick={() => { if (!customStartDate || !customEndDate) { toast.warning('Seleccione ambas fechas'); return; } fetchReportes(); }}
                        >
                            Aplicar
                        </button>
                    </div>
                )}
            </div>

            {/* Filters */}
            <div className="admin-filters">
                <div className="admin-search">
                    <input
                        type="text"
                        placeholder="Buscar por codigo, nombre, descripcion..."
                        value={searchTerm}
                        onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                    />
                </div>
                <div className="admin-filter-row">
                    <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}>
                        <option value="all">Todos los estados</option>
                        {Object.keys(STATUS_COLORS).map(s => (
                            <option key={s} value={s}>{s}</option>
                        ))}
                    </select>
                    <select value={categoryFilter} onChange={(e) => { setCategoryFilter(e.target.value); setCurrentPage(1); }}>
                        <option value="all">Todas las categorias</option>
                        {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                            <option key={k} value={k}>{v}</option>
                        ))}
                    </select>
                    <select value={priorityFilter} onChange={(e) => { setPriorityFilter(e.target.value); setCurrentPage(1); }}>
                        <option value="all">Todas las prioridades</option>
                        <option value="baja">Baja</option>
                        <option value="media">Media</option>
                        <option value="alta">Alta</option>
                    </select>
                </div>
            </div>

            {/* Content */}
            {loading ? (
                <div className="admin-loading">
                    <div className="spinner-reportes"></div>
                    <p>Cargando reportes...</p>
                </div>
            ) : viewMode === 'mapa' ? (
                <MapaReportes
                    reportes={filteredReportes}
                    sectors={sectors}
                    onSelectReporte={(id) => {
                        const r = reportes.find(rep => rep._id === id);
                        if (r) setSelectedReporte(r);
                    }}
                />
            ) : viewMode === 'dashboard' ? (
                /* Dashboard view - cards */
                <div className="reportes-dashboard-grid">
                    {filteredReportes.length === 0 ? (
                        <div className="no-data-cell" style={{ gridColumn: '1/-1', textAlign: 'center', padding: '40px' }}>
                            No hay reportes con los filtros seleccionados
                        </div>
                    ) : (
                        filteredReportes.map(r => (
                            <div key={r._id} className="reporte-dashboard-card" onClick={() => setSelectedReporte(r)}>
                                <div className="rdc-header">
                                    <code className="tracking-code-sm">{r.trackingCode}</code>
                                    <span className="status-pill-admin" style={{
                                        background: STATUS_COLORS[r.status], color: 'white'
                                    }}>{r.status}</span>
                                </div>
                                <div className="rdc-body">
                                    <h4>{r.reporterName}</h4>
                                    <p className="rdc-description">{r.description?.substring(0, 80)}{r.description?.length > 80 ? '...' : ''}</p>
                                    <div className="rdc-categories">
                                        {(r.categories || []).map(c => (
                                            <span key={c} className="cat-tag-sm">{CATEGORY_LABELS[c] || c}</span>
                                        ))}
                                    </div>
                                </div>
                                <div className="rdc-footer">
                                    <span className="rdc-location">{r.locationName || r.sector?.name || '-'}</span>
                                    <span className="rdc-date">{new Date(r.createdAt).toLocaleDateString('es-ES')}</span>
                                    {(r.assignments || []).length > 0 && (
                                        <span className="rdc-brigades">{r.assignments.length} brigada{r.assignments.length > 1 ? 's' : ''}</span>
                                    )}
                                </div>
                                {r.priority === 'alta' && <div className="rdc-priority-badge">ALTA</div>}
                            </div>
                        ))
                    )}
                </div>
            ) : (
                /* Table view */
                <>
                    <div className="admin-table-wrapper">
                        <table className="admin-reportes-table">
                            <thead>
                                <tr>
                                    <th>Codigo</th>
                                    <th>Reportante</th>
                                    <th>Categoria</th>
                                    <th>Ubicacion</th>
                                    <th>Estado</th>
                                    <th>Brigadas</th>
                                    <th>Prioridad</th>
                                    <th>Fecha</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedReportes.map(r => (
                                    <tr key={r._id} onClick={() => setSelectedReporte(r)} className="clickable-row">
                                        <td><code className="tracking-code-sm">{r.trackingCode}</code></td>
                                        <td>{r.reporterName}</td>
                                        <td>
                                            <div className="categories-cell">
                                                {(r.categories || []).map(c => (
                                                    <span key={c} className="cat-tag-sm">{CATEGORY_LABELS[c] || c}</span>
                                                ))}
                                            </div>
                                        </td>
                                        <td>{r.locationName || r.sector?.name || '-'}</td>
                                        <td>
                                            <span className="status-pill-admin" style={{
                                                background: STATUS_COLORS[r.status],
                                                color: 'white'
                                            }}>
                                                {r.status}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="brigades-cell">
                                                {(r.assignments || []).length > 0 ? (
                                                    (r.assignments || []).map((a, i) => (
                                                        <span key={i} className="brigade-tag-sm" style={{
                                                            borderColor: PRIORITY_COLORS[a.priority] || '#ddd'
                                                        }}>
                                                            {a.brigade?.name || '?'}
                                                        </span>
                                                    ))
                                                ) : (
                                                    <span className="no-brigade">-</span>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <span className="priority-pill" style={{
                                                color: PRIORITY_COLORS[r.priority],
                                                borderColor: PRIORITY_COLORS[r.priority]
                                            }}>
                                                {PRIORITY_LABELS[r.priority]}
                                            </span>
                                        </td>
                                        <td>{new Date(r.createdAt).toLocaleDateString('es-ES')}</td>
                                        <td>
                                            <button
                                                className="btn-view-detail"
                                                onClick={(e) => { e.stopPropagation(); setSelectedReporte(r); }}
                                            >
                                                Ver
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {paginatedReportes.length === 0 && (
                                    <tr>
                                        <td colSpan="9" className="no-data-cell">
                                            No hay reportes con los filtros seleccionados
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {totalPages > 1 && (
                        <div className="admin-pagination">
                            <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>Anterior</button>
                            <span>Pagina {currentPage} de {totalPages} ({filteredReportes.length} reportes)</span>
                            <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>Siguiente</button>
                        </div>
                    )}
                </>
            )}

            {/* Detail Modal */}
            {selectedReporte && (
                <ReporteDetailModal
                    reporte={selectedReporte}
                    brigades={brigades}
                    onClose={() => setSelectedReporte(null)}
                    onStatusChange={canChangeStatus ? handleStatusChange : () => toast.warning('Sin permisos para cambiar estado')}
                    onAddAssignment={canAssign ? handleAddAssignment : () => toast.warning('Sin permisos para asignar')}
                    onRemoveAssignment={canAssign ? handleRemoveAssignment : () => toast.warning('Sin permisos')}
                    onUpdateAssignmentStatus={handleUpdateAssignmentStatus}
                    onAddComment={canComment ? handleAddComment : () => toast.warning('Sin permisos para comentar')}
                    onExportPDF={canExport ? generateReportePDF : null}
                    onDelete={canDelete ? handleDeleteReporte : null}
                />
            )}
        </div>
    );
};

export default AdminReportesCiudadanos;
